const TEXT_SELECTION_ALLOWED_SELECTOR = [
  'input',
  'textarea',
  'select',
  '[role="textbox"]',
  '[contenteditable]',
  '[data-allow-text-selection]',
].join(',');

export interface GameSelectionGuardHandle {
  dispose(): void;
}

function elementFromTarget(target: EventTarget | null): Element | null {
  if (target instanceof Element) return target;
  if (target instanceof Node) return target.parentElement;
  return null;
}

export function isTextSelectionAllowedTarget(target: EventTarget | null): boolean {
  const element = elementFromTarget(target);
  if (element == null) return false;
  if (element instanceof HTMLElement && element.isContentEditable) return true;
  return element.closest(TEXT_SELECTION_ALLOWED_SELECTOR) != null;
}

function getSelectionForTarget(target: EventTarget | null): Selection | null {
  const node = target instanceof Node ? target : null;
  const documentRef = node?.ownerDocument ?? document;
  return documentRef.defaultView?.getSelection?.() ?? documentRef.getSelection?.() ?? null;
}

function clearSelection(target: EventTarget | null): void {
  const selection = getSelectionForTarget(target);
  if (selection == null || selection.rangeCount === 0) return;
  selection.removeAllRanges();
}

function normalizeRoots(root: EventTarget | readonly EventTarget[]): readonly EventTarget[] {
  return Array.isArray(root) ? root : [root as EventTarget];
}

export function installGameSelectionGuard(
  root: EventTarget | readonly EventTarget[],
): GameSelectionGuardHandle {
  const roots = normalizeRoots(root);

  const handleSelectStart = (event: Event): void => {
    if (isTextSelectionAllowedTarget(event.target)) return;
    event.preventDefault();
  };

  const handleDragStart = (event: Event): void => {
    if (isTextSelectionAllowedTarget(event.target)) return;
    event.preventDefault();
  };

  const handlePointerDown = (event: Event): void => {
    if (isTextSelectionAllowedTarget(event.target)) return;
    clearSelection(event.target);
  };

  for (const currentRoot of roots) {
    currentRoot.addEventListener('selectstart', handleSelectStart, { capture: true });
    currentRoot.addEventListener('dragstart', handleDragStart, { capture: true });
    currentRoot.addEventListener('pointerdown', handlePointerDown, { capture: true });
  }

  return {
    dispose(): void {
      for (const currentRoot of roots) {
        currentRoot.removeEventListener('selectstart', handleSelectStart, { capture: true });
        currentRoot.removeEventListener('dragstart', handleDragStart, { capture: true });
        currentRoot.removeEventListener('pointerdown', handlePointerDown, { capture: true });
      }
    },
  };
}
